import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Phone, Star, Leaf, MapPin, Heart } from "lucide-react";
import { MaskLine } from "./Reveal";
import { useMotionOff } from "@/context/A11yContext";
import { IMAGES, CONTACT } from "./data";

const TRUST = [
  { icon: Leaf, label: "100% eco-safe products" },
  { icon: Star, label: "4.9/5 from happy clients" },
  { icon: MapPin, label: CONTACT.area },
];

export const Hero = () => {
  const ref = useRef(null);
  const off = useMotionOff();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, off ? 0 : 90]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} aria-labelledby="hero-heading" className="relative bg-brand-cream pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      {/* soft organic blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 h-[32rem] w-[32rem] rounded-full bg-brand-green/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-brand-blue/10 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr,0.9fr] gap-10 lg:gap-16 items-center">
          <div>
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-line px-4 py-2 text-sm font-bold text-brand-accentink shadow-sm">
              <Heart className="h-4 w-4 text-brand-green fill-brand-green" aria-hidden="true" /> Cleaning with heart
            </motion.span>

            <h1 id="hero-heading" className="mt-6 font-heading font-extrabold tracking-tight leading-[1.08] text-brand-ink text-[clamp(2.5rem,6vw,5rem)]">
              <MaskLine delay={0.1}>Care for your</MaskLine>
              <MaskLine delay={0.22} className="text-brand-blue">home & the people</MaskLine>
              <MaskLine delay={0.34}>who live in it.</MaskLine>
            </h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }} className="mt-6 max-w-xl text-lg sm:text-xl text-brand-ink2 leading-relaxed">
              Friendly, vetted cleaners and non-toxic, eco-friendly products — for homes, offices,
              end-of-lease and vehicles across Australia.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.62 }} className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="hero-quote-cta" onClick={() => go("#quote")} className="group flex items-center gap-2 rounded-full bg-brand-blue text-white text-base font-bold pl-6 pr-3 py-3.5 hover:bg-brand-bluehover transition-colors duration-300">
                Get your free quote
                <span className="grid place-items-center h-8 w-8 rounded-full bg-brand-green text-brand-ink transition-transform duration-300 group-hover:translate-x-0.5"><ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" /></span>
              </button>
              <a href={CONTACT.phoneHref} data-testid="hero-phone-cta" className="flex items-center gap-2 rounded-full border-2 border-brand-blue text-brand-blue text-base font-bold px-6 py-3.5 hover:bg-brand-blue hover:text-white transition-colors duration-300">
                <Phone className="h-4 w-4" strokeWidth={2} aria-hidden="true" /> {CONTACT.phone}
              </a>
            </motion.div>

            <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.8 }} className="mt-9 flex flex-wrap gap-x-6 gap-y-3">
              {TRUST.map((t) => (
                <li key={t.label} className="flex items-center gap-2 text-sm font-semibold text-brand-ink2">
                  <t.icon className="h-4 w-4 text-brand-green" strokeWidth={2} aria-hidden="true" /> {t.label}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-[0_30px_70px_-30px_rgba(2,71,157,0.4)]">
              <motion.img style={{ y }} src={IMAGES.hero} alt="A friendly Aid in Clean cleaner holding eco-friendly supplies in a bright living room" className="h-[110%] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 rounded-3xl bg-white shadow-xl border border-brand-line px-5 py-4 flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-brand-green/15 text-brand-accentink"><Leaf className="h-6 w-6" strokeWidth={2} aria-hidden="true" /></span>
              <div>
                <div className="text-lg font-heading font-extrabold text-brand-blue leading-tight">3,500+</div>
                <div className="text-xs font-semibold text-brand-ink2">homes cared for</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
